import { Button, Dropdown, InputRef, Menu, Row, Spin, Switch, Tag } from "antd";
import { PaginationProps } from "antd/es/pagination";
import type { ColumnType, ColumnsType } from "antd/es/table";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  AiOutlineDelete,
  AiOutlineHistory,
  AiOutlinePrinter,
} from "react-icons/ai";
import { BiTrip } from "react-icons/bi";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { filterFunction } from "../../common/SearchFilterComponent";

import { TbListDetails } from "react-icons/tb";
import { deleteLabel, getUniqueId } from "../../common/common";
import {
  ITripPlan,
  ITripPlanFilterData,
  ITripPlanStore,
} from "../../model/TripPlanModel";
import { useTripPlanStore } from "../../stores/TripPlanStore";
import {
  CountryEnum,
  Employee,
  IS_CANCELLED_STATUS_ID,
  TRIP_OPEN,
  TripPlanStatusEnum,
  UserEnum,
  isAccessible,
} from "../../utils/app-common";
import { convertDateFromMillis, convertDateIntoMillis } from "../../utils/luxon";
import ConfirmationModal from "../CommonComponent/ConfirmationModal";
import TableComponent from "../CommonComponent/TableComponent";
import { TripHistoryComponent } from "./TripHistoryComponent";
import { TripPlanFilterFilter } from "../CommonComponent/TripplanFilter/TripPlanFilter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterPaginatedTripPlanService } from "../../services/trip-plan-service";
import { useTableStore } from "../../stores/TableFilterStore";
import { useUserContextHook } from "../../hooks/useUserContextHook";
import DashboardRow from "./Dashboard";
import { DownOutlined } from "@ant-design/icons";
import { FaMapMarked } from "react-icons/fa";
import { TrackVehicleService } from "../../services/trip-plan-service";
import { TrackTrip } from "./Tracking";
import moment from "moment";

import './Trip.css'
import { getNotificationStyle } from "../../common/notification";
import { TrackTripOld } from "./TrackingOld";
import { isAllBusinessUser } from "../../utils/User-constants";

const tripPlanStatusColorMap: Record<string, string> = {
  Open: "blue",
  Cancelled: "red",
  Closed: "green",
  "Load To Destination": "orange",
  "Offload At Transit": "volcano",
  "Load At Transit": "purple",
  "Arrived At Destination": "cyan",
};

const EMPTY_TRIP_LOAD_TYPE = 1;
const HEAD_ONLY_LOAD_TYPE = 3;

export enum TripDirection {
  Outgoing = "Outgoing",
  Incoming = "Incoming",
}

export const isTripPlanHistoryStatus = [
  TripPlanStatusEnum.ArrivedAtDestination,
  TripPlanStatusEnum.Closed,
  TripPlanStatusEnum.LoadToDestination,
  TripPlanStatusEnum.LoadAtTransit,
  TripPlanStatusEnum.OffloadAtTransit,

]
export const isValidOptimizedTripStatus = [
  TripPlanStatusEnum.LoadToDestination,
  TripPlanStatusEnum.LoadAtTransit,
  TripPlanStatusEnum.OffloadAtTransit,

]

export const useTrackVehicleQuery = ({ plateNo, enabled }: { plateNo: string; enabled: boolean }) => {
  const onRequest = async () => {
    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

    const filterData = {
      id: plateNo,
      date: today,
      filterType: "id"
    };

    const res = await TrackVehicleService(filterData);
    if (res?.status === 200 && res?.data?.statusCode === 200) {

      return res.data?.data?.data?.[0] || {};
    } else {
      return { error: "Failed to track vehicle" };
    }
  };

  return useQuery({
    queryKey: ["track-vehicle", plateNo],
    queryFn: () => onRequest(),
    enabled: !!plateNo && enabled,
  });
};


export const useGetTripPlanListSearchFilterQuery = ({ filter, pageNumber, pageSize }) => {

  const onRequest = async () => {
    const [startDate, endDate] = filter?.startEndDate || [];
    const tripPlanFilterData: ITripPlanFilterData = {
      ...filter,
       startDate: startDate
        ? convertDateIntoMillis(moment(startDate).subtract(1, 'day'))
        : undefined,
      endDate: endDate ? convertDateIntoMillis(endDate) : undefined,
    };
    const res = await FilterPaginatedTripPlanService(pageNumber, pageSize, tripPlanFilterData);
    if (res?.status === 200 && res?.data?.statusCode === 200) {
      return res.data;
    } else {
      return [];
    }
  };
  return useQuery({
    queryKey: ['trip-plan-table-filter', filter, pageNumber, pageSize],
    queryFn: () => onRequest(),
    staleTime: 60000,
    enabled: !!pageNumber && !!pageSize,
  });
};

const MenuList = ({ row }: { row: ITripPlan }) => {
  const queryClient = useQueryClient();
  const [showTrackTrip, setShowTrackTrip] = useState(false);
  const [showTrackTripOld, setShowTrackTripOld] = useState(false);

  const DeleteTripPlanByIdAction = useTripPlanStore(
    (state: ITripPlanStore) => state?.DeleteTripPlanByIdAction
  );
  const PrintTripPlanAction = useTripPlanStore(
    (state: ITripPlanStore) => state?.PrintTripPlanAction
  );
  const PrintVehicleManifestAction = useTripPlanStore(
    (state: ITripPlanStore) => state?.PrintVehicleManifestAction
  );
  const PrintTripSheetAction = useTripPlanStore(
    (state: ITripPlanStore) => state?.PrintTripSheetAction
  );
  const PrintManifestSheetAction = useTripPlanStore(
    (state: ITripPlanStore) => state?.PrintManifestSheetAction
  );
  const navigate: NavigateFunction = useNavigate();

  const { promiseInProgress: deleteTripPlanProgress } = usePromiseTracker({
    area: "delete-trip-plan-area",
  });
  const { promiseInProgress: printTripProgress } = usePromiseTracker({
    area: "print-trip-area",
  });
  const [printingKey, setPrintingKey] = useState<number | null>(null);

  const [isTripHistoryModalOpen, setIsTripHistoryModalOpen] = useState(false);

  const [isConfirmationModal, setIsConfirmationModal] =
    useState<boolean>(false);
  const onDeleteCustomerHandle = useCallback(() => {
    setIsConfirmationModal(true);
  }, []);

  const onDetailCustomerHandle = useCallback(() => {
    useTripPlanStore.setState({ tripPlan: {} });
    navigate(`/trip/trip-plan/${row?.id}`, { state: row });
  }, [navigate, row]);

  const onConfirmedClicked = useCallback(async () => {
    await trackPromise(DeleteTripPlanByIdAction(row?.id!), "delete-trip-plan-area");
    queryClient.invalidateQueries({ queryKey: ["trip-plan-table-filter"], exact: false });
    queryClient.invalidateQueries({ queryKey: ["trip-plan-dashboard-count"], exact: false });
    setIsConfirmationModal(false);
  }, [DeleteTripPlanByIdAction, queryClient, row?.id]);

  const onTripHistoryHandle = useCallback(() => {
    setIsTripHistoryModalOpen(true);
  }, []);

  const onPrintTripHandler = async () => {
    setPrintingKey(4);
    await trackPromise(PrintTripPlanAction(row?.id), "print-trip-area");
    setPrintingKey(null);
  };
  const onPrintVehicleManifestHandler = async () => {
    setPrintingKey(5);
    await trackPromise(PrintVehicleManifestAction(row?.id), "print-trip-area");
    setPrintingKey(null);
  };
  const onPrintTripSheetHandler = async () => {
    setPrintingKey(8);
    await trackPromise(PrintTripSheetAction(row?.id), "print-trip-area");
    setPrintingKey(null);
  };
  const onPrintManifestSheetHandler = async () => {
    setPrintingKey(9);
    await trackPromise(PrintManifestSheetAction(row?.id), "print-trip-area");
    setPrintingKey(null);
  };



  return (
    <>
      <Menu
        items={[
          {
            key: 1,
            label: "Details",
            icon: <TbListDetails size={18} />,
            onClick: () => onDetailCustomerHandle(),
          },
          !isAccessible([UserEnum.MANIFESTER, UserEnum.DATA_ANALYST, UserEnum.OCC_SOLUTION, UserEnum.OPERATION_CS_SUPERVISOR_MANIFESTER,])
            ? {
              key: 2,
              danger: true,
              label: "Cancel",
              title: "Cancel",
              disabled:
                row?.isClosed || row?.statusId === IS_CANCELLED_STATUS_ID || row?.tripPlanStatus !== TRIP_OPEN,
              icon: <AiOutlineDelete size={18} />,
              onClick: () => onDeleteCustomerHandle(),
            }
            : null,
          {
            key: 3,
            label: "Trip History",
            title: "Trip History",
            icon: <AiOutlineHistory size={18} />,
            onClick: () => onTripHistoryHandle(),
          },
          {
            key: 8,
            label: "Trip Sheet",
            title: "Trip Sheet",
            icon: printingKey === 8 ? <Spin size="small" /> : <AiOutlinePrinter size={18} />,
            disabled: printTripProgress,
            onClick: () => onPrintTripSheetHandler(),
          },
          {
            key: 9,
            label: "Manifest Sheet",
            title: "Manifest Sheet",
            icon: printingKey === 9 ? <Spin size="small" /> : <AiOutlinePrinter size={18} />,
            disabled: printTripProgress,
            onClick: () => onPrintManifestSheetHandler(),
          },
          {
            key: 4,
            label: "Print Trip",
            title: "Print Trip",
            icon: printingKey === 4 ? <Spin size="small" /> : <AiOutlinePrinter size={18} />,
            disabled: printTripProgress,
            onClick: () => onPrintTripHandler(),
          },
          {
            key: 5,
            label: "Vehicle Manifest Report",
            title: "Vehicle Manifest Report",
            icon: printingKey === 5 ? <Spin size="small" /> : <AiOutlinePrinter size={18} />,
            disabled: printTripProgress,
            onClick: () => onPrintVehicleManifestHandler(),
          },
          ...(
            (row?.vehicleId && isTripPlanHistoryStatus?.includes(row?.tripPlanStatus as TripPlanStatusEnum)) ?
              [{
                key: 7,
                label: isTripPlanHistoryStatus?.includes(row?.tripPlanStatus as TripPlanStatusEnum) && row?.vehicleTrackingNumber ? "Track Trip" : "Tracking not available",
                title: isTripPlanHistoryStatus?.includes(row?.tripPlanStatus as TripPlanStatusEnum) && row?.vehicleTrackingNumber ? "Track Trip" : "Tracking not available",
                icon: <FaMapMarked size={18} />,
                onClick: () => setShowTrackTripOld(!showTrackTrip),
                disabled: isTripPlanHistoryStatus?.includes(row?.tripPlanStatus as TripPlanStatusEnum) && row?.vehicleTrackingNumber ? false : true,

              }] : []
          ),

          ...(
            isAccessible(
              [
                UserEnum.OCC_SOLUTION,
                UserEnum.MANAGER,
                UserEnum.OPS_MANAGER,
                UserEnum.OPERATION_SUPERVISOR,
                UserEnum.OPERATION_SUPERVISOR_CASH_COLLECTOR,
                UserEnum.OPERATION_SUPERVISOR_OPERATION_STAFF,
              ],
              [CountryEnum.BAHRAIN, CountryEnum.KSA, CountryEnum.UAE]
            ) && isValidOptimizedTripStatus?.includes(row?.tripPlanStatus as TripPlanStatusEnum) ? [{
              key: 6,
              label: "Optimized Trip Route",
              title: "Optimized Trip Route",
              icon: <FaMapMarked size={18} />,
              onClick: () => setShowTrackTrip(!showTrackTrip),
              disabled: isTripPlanHistoryStatus?.includes(row?.tripPlanStatus as TripPlanStatusEnum) ? false : true,

            }] : []
          ),



        ]}
      />
      {isConfirmationModal ? (
        <ConfirmationModal
          key={getUniqueId()}
          promiseInProgress={deleteTripPlanProgress}
          openModal={isConfirmationModal}
          confirmClicked={onConfirmedClicked}
          hideModal={() => setIsConfirmationModal(false)}
          label={deleteLabel(`${row?.tripPlanNumber!}`)}
        ></ConfirmationModal>
      ) : null}
      {isTripHistoryModalOpen ? (
        <TripHistoryComponent
          isOpen={isTripHistoryModalOpen}
          closeModal={() => setIsTripHistoryModalOpen(false)}
          tripCode={row?.tripPlanNumber}
          tripId={row?.id}
        />
      ) : null}
      {showTrackTrip && (
        <TrackTrip visible={showTrackTrip} handleCancel={() => setShowTrackTrip(false)} record={row} />
      )}
      {showTrackTripOld && (
        <TrackTripOld visible={showTrackTripOld} handleCancel={() => setShowTrackTripOld(false)} record={row} />
      )}
    </>
  );
};
const CreateCustomerComponent = ({
  onCreateCustomerHandle,
}: {
  onCreateCustomerHandle: () => void;
}) => {
  return (
    <Button
      size="small"
      onClick={onCreateCustomerHandle}
      icon={<BiTrip size={18} />}
      className="icon-button"
      hidden={isAccessible([UserEnum.MANIFESTER, UserEnum.DATA_ANALYST, UserEnum.OCC_SOLUTION,])}
    >
      Create Trip Plan
    </Button>
  );
};

const TripPlanTableComponent = () => {

  const queryClient = useQueryClient();
  const { employeeDetail } = useUserContextHook();
  const { businessId: employeeBusinessId } = employeeDetail ?? {};
  const {
    tripPlanTable: { filter, pageNumber, pageSize },
    setTripPlanTableState,
  } = useTableStore();


  const defaultIndustryTypeList = useMemo(() => {
    const id = Number(employeeBusinessId);
    if (!id || id === 9) return undefined;   // 9 → no business-unit filter
    return [id];                             // 8 → [8], 2 → [2]
  }, [employeeBusinessId]);

  const initialValue = useMemo(() => {
    const today = new Date();
    const prevDate = new Date(today);
    prevDate.setDate(today.getDate() - 1);

    return {
      originList: employeeDetail?.stationCityId !== undefined ? [employeeDetail.stationCityId] : [],
      startEndDate: [prevDate, today],
      tripDirection: TripDirection.Outgoing,
      industryTypeList: defaultIndustryTypeList,
    };
  }, [employeeDetail?.stationCityId, defaultIndustryTypeList]);


  const searchInput = useRef<InputRef>(null);

  const getColumnSearchProps = (dataIndex: any): ColumnType<ITripPlan> =>
    filterFunction(dataIndex, searchInput);

  const renderSlaTag = (text: string | null | undefined) => {
    if (!text) return "--";
    return (
      <Tag color={text.toLowerCase().includes("delay") ? "orange" : "green"}>
        {text}
      </Tag>
    );
  };



  const formatTime = (value: string | null | undefined) => {
    if (!value) return "--";
    const [hh, mm] = value.split(":");
    if (!hh || !mm) return "--";
    return `${hh.padStart(2, "0")}:${mm.padStart(2, "0")}`;
  };




  const renderStatusTag = (status: string | null | undefined) => {
    if (!status) return "--";
    const lower = status.toLowerCase();
    const color = lower.includes("delay")
      ? "orange"
      : lower.includes("on time")
        ? "green"
        : tripPlanStatusColorMap[status] || "default";
    return (
      <Tag
        color={color}
        style={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "center",
          padding: "2px 6px",
        }}
      >
        {status}
      </Tag>
    );
  };

  const tripPlanColumns: ColumnsType<ITripPlan> = [
    {
      title: "Trip Plan Number",
      dataIndex: "tripPlanNumber",
      fixed: "left",
      key: "tripPlanNumber",
      width: 130,
      ...getColumnSearchProps("tripPlanNumber"),
    },
    {
      title: "Trip Code",
      dataIndex: "tripCode",
      fixed: "left",
      key: "tripCode",
      width: 110,
      ...getColumnSearchProps("tripCode"),
    },
    {
      title: "Origin",
      dataIndex: "originName",
      key: "originName",
      width: 100,
      ...getColumnSearchProps("originName"),
      render: (originName: string | null | undefined) => originName || "--",
    },
    {
      title: "Destination",
      dataIndex: "destinationName",
      key: "destinationName",
      width: 100,
      ...getColumnSearchProps("destinationName"),
      render: (destinationName: string | null | undefined) => destinationName || "--",
    },
    {
      title: "Trip Type",
      dataIndex: "tripTypename",
      key: "tripTypename",
      width: 100,
      ...getColumnSearchProps("tripTypename"),
    },
    {
      title: "Departure",
      align: "center",
      onHeaderCell: () => ({ className: "trip-group-header trip-group-departure" }),
      children: [
        {
          title: "STD",
          dataIndex: "tripRouteEtd",
          key: "std",
          width: 80,
          align: "center",
          className: "trip-group-col-left",
          onHeaderCell: () => ({ className: "trip-group-col-left" }),
          render: (etd: string) => formatTime(etd),
        },
        {
          title: "ATD",
          dataIndex: "tripAtd",
          key: "tripAtd",
          width: 80,
          align: "center",
          render: (tripAtd: number) => tripAtd ? convertDateFromMillis(tripAtd, "HH:mm") : "--",
        },
        {
          title: "Status",
          dataIndex: "depSla",
          key: "depSla",
          width: 90,
          align: "center",
          className: "trip-group-col-right",
          onHeaderCell: () => ({ className: "trip-group-col-right" }),
          ...getColumnSearchProps("depSla"),
          render: renderSlaTag,
        },
      ],
    },
    {
      title: "Arrival",
      align: "center",
      onHeaderCell: () => ({ className: "trip-group-header trip-group-arrival" }),
      children: [
        {
          title: "STA",
          dataIndex: "tripRouteEta",
          key: "sta",
          width: 80,
          align: "center",
          className: "trip-group-col-left",
          onHeaderCell: () => ({ className: "trip-group-col-left" }),
          render: (etd: string) => formatTime(etd),
        },
        {
          title: "ATA",
          dataIndex: "tripAta",
          key: "tripAta",
          width: 80,
          align: "center",
          render: (ata: number) => ata ? convertDateFromMillis(ata, "HH:mm") : "--",
        },
        {
          title: "Status",
          dataIndex: "arrSla",
          key: "arrSla",
          width: 90,
          align: "center",
          className: "trip-group-col-right",
          onHeaderCell: () => ({ className: "trip-group-col-right" }),
          ...getColumnSearchProps("arrSla"),
          render: renderSlaTag,
        },
      ],
    },
    {
      title: "Dep Date",
      dataIndex: "etd",
      key: "depDate",
      width: 100,
      render: (etd: number) => convertDateFromMillis(etd, "dd-MM-yyyy"),
    },
    {
      title: "Arr Date",
      dataIndex: "eta",
      key: "arrDate",
      width: 100,
      render: (eta: number) => convertDateFromMillis(eta, "dd-MM-yyyy"),
    },
    {
      title: "Trip Status",
      dataIndex: "tripPlanStatus",
      key: "tripPlanStatus",
      width: 120,
      ...getColumnSearchProps("tripPlanStatus"),
      render: renderStatusTag,
    },
    // {
    //   title: "Status",
    //   dataIndex: "currentStatus",
    //   key: "currentStatus",
    //   width: 110,
    //   ...getColumnSearchProps("currentStatus"),
    //   render: (status: string | null | undefined) => status || "--",
    // },
    {
      title: "LH Exception",
      dataIndex: "lhExceptionPresent",
      key: "lhExceptionPresent",
      width: 100,
      align: "center",
      render: (lhExceptionPresent: boolean | null | undefined) => (
        <Tag color={lhExceptionPresent ? "red" : "default"}>
          {lhExceptionPresent ? "Y" : "N"}
        </Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createAt",
      key: "createAt",
      width: 100,
      render: (createAt: number) => convertDateFromMillis(createAt, "dd-MM-yyyy HH:mm"),
    },
    {
      title: "Trip Load Type",
      dataIndex: "tripLoadTypeName",
      key: "tripLoadTypeName",
      width: 100,
      ...getColumnSearchProps("tripLoadTypeName"),
    },
    {
      title: "Plate Number",
      dataIndex: "plateNumber",
      key: "plateNumber",
      width: 100,
      ...getColumnSearchProps("plateNumber"),
      render: (plateNumber: string | null | undefined) => plateNumber || "--",
    },
    {
      title: "Trailer Fleet ID",
      dataIndex: "trailerNumber",
      key: "trailerNumber",
      width: 100,
      ...getColumnSearchProps("trailerNumber"),
      render: (trailerNumber: string | null | undefined) => trailerNumber || "--",
    },
    {
      title: "Start Odometer",
      dataIndex: "startOdometer",
      key: "startOdometer",
      width: 100,
      ...getColumnSearchProps("startOdometer"),
      render: (startOdometer: number | null | undefined) => startOdometer || "--",
    },
    {
      title: "End Odometer",
      dataIndex: "closeOdometer",
      key: "closeOdometer",
      width: 100,
      ...getColumnSearchProps("closeOdometer"),
      render: (closeOdometer: number | null | undefined) => closeOdometer || "--",
    },
    {
      title: "Created by",
      dataIndex: "createdByEmployeeName",
      key: "createdByEmployeeName",
      width: 100,
      ...getColumnSearchProps("createdByEmployeeName"),
    },


    {
      title: "Business Unit",
      dataIndex: "divisionName",
      key: "divisionName",
      width: 100,
      ...getColumnSearchProps("divisionName"),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 85,
      render: (row) => {
        return (
          <>
            <Dropdown
              overlay={<MenuList row={row} />}
              trigger={['click']}
            >
              <Button size="small" className="icon-button" type="text">
                More <DownOutlined />
              </Button></Dropdown>
          </>
        );
      },
    },
  ];

  const onPaginationChange: PaginationProps["onChange"] = (page: number) => {
    setTripPlanTableState({ pageNumber: page });
  };


  const onResetClicked = useCallback(() => {
    setTripPlanTableState({ pageNumber: 1, filter: initialValue });
  }, [initialValue, setTripPlanTableState]);

  const handleSearch = async (values: any) => {
    // Validate startEndDate range
    if (values?.startEndDate && values?.startEndDate?.length === 2) {
      const [start, end] = values?.startEndDate;
      const startDate = moment(start);
      const endDate = moment(end);
      const diffDays = endDate?.diff(startDate, 'days');
      if (diffDays > 45) {
        getNotificationStyle({ message: "Date range cannot be more than 45 days", type: "error" });
        return;
      }
    }

    const industryTypeList = isAllBusinessUser()
      ? values?.industryTypeList
      : defaultIndustryTypeList;

    if (industryTypeList?.length) {
      const tempList: number[] = [];
      if (industryTypeList.some(item => Number(item) === 6)) tempList.push(1, 2, 9);
      if (industryTypeList.some(item => Number(item) === 7)) tempList.push(7);
      values.divisionIds = tempList;
    }


    setTripPlanTableState({ filter: { ...filter, ...values, industryTypeList }, pageNumber: 1 });
  };


  const { promiseInProgress: printTripProgress } = usePromiseTracker({
    area: "print-trip-area",
  });

  const { data: tripPlanList, isFetching, refetch } = useGetTripPlanListSearchFilterQuery({
    filter: filter?.originList ? filter : initialValue,
    pageNumber,
    pageSize,
  });
  const navigate: NavigateFunction = useNavigate();
  const onCreateCustomerHandle = () => {
    navigate("/trip/add-trip-plan");
  };

  const refreshHandler = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["trip-plan-dashboard-count"], exact: false });
    refetch();
  }, [queryClient, refetch]);



  return (
    <>
      {/* <SearchFilterComponent
        onSearchClicked={onSearchHandler}
        onResetClicked={getTripPlanData}
        label="Trip Plan Number"
        name="tripplanNumber"
      >
        <Col span={3} md={6} lg={4} xl={4} sm={8} xs={8}>
          <Form.Item
            name="tripRoute"
            label={"Trip Route"}
            labelCol={{ span: 24 }}
          >
            <Input allowClear autoComplete={"off"} />
          </Form.Item>
        </Col>
      </SearchFilterComponent> */}


      <TripPlanFilterFilter
        onSearch={handleSearch}
        onReset={onResetClicked}
        refresh={refreshHandler}
        initialValues={filter?.originList ? filter : initialValue}
        title="Trip Plan "
        children={<>
        </>}
      />
      {
        filter?.tripDirection === TripDirection.Outgoing ? (<DashboardRow
          filter={filter?.originList ? filter : initialValue}
          data={tripPlanList?.data ?? []}
        />) : null
      }

      <>
        <div style={{ marginBottom: 12, textAlign: "left" }}>
          <Row>
            <Switch
              style={{ marginRight: 15 }}
              className="custom-switch"
              checkedChildren="Outgoing"
              unCheckedChildren="Incoming"
              checked={filter?.tripDirection === TripDirection.Outgoing}
              onChange={(checked) => {
                setTripPlanTableState({
                  filter: {
                    ...filter,
                    tripDirection: checked ? TripDirection.Outgoing : TripDirection.Incoming,
                    originList: filter?.destinationList || [],
                    destinationList: filter?.originList || initialValue?.originList,
                    startEndDate: filter?.startEndDate || initialValue?.startEndDate,
                  },
                  pageNumber: 1,
                });
              }}
            />
            <CreateCustomerComponent
              onCreateCustomerHandle={onCreateCustomerHandle}
            />
          </Row>

        </div>
      </>

      <TableComponent
        scroll={{ y: 550, x: 2500 }}
        columns={tripPlanColumns}
        data={tripPlanList?.data ?? []}
        pageNumber={pageNumber}
        pageSize={pageSize}
        total={tripPlanList?.totalNumberOfAllElements ?? 1}
        isLoading={isFetching || printTripProgress}
        onPaginationChange={onPaginationChange}
        onRefreshHandle={onResetClicked}
        current={pageNumber}
        isRefresh={false}
        size="small"
      >
        <>

        </>
      </TableComponent>
    </>
  );
};

TripPlanTableComponent.propTypes = {};

export default TripPlanTableComponent;
